import {
    Component,
    ViewChild,
    OnDestroy,
    OnInit,
    ChangeDetectorRef,
    Injectable,
  } from "@angular/core";
  import { Subscription, Observable, of } from "rxjs";
  import { catchError } from "rxjs/operators";
  import { HttpClient, HttpHeaders } from "@angular/common/http";
  import { environment } from "src/environments/environment";
  import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
  import {
    ApexChart,
    ApexAxisChartSeries,
    ApexDataLabels,
    ApexPlotOptions,
    ApexYAxis,
    ApexLegend,
    ApexStroke,
    ApexXAxis,
    ApexFill,
    ApexTooltip,
    ApexStates,
    ApexNonAxisChartSeries,
    ApexResponsive,
    ChartComponent,
    ApexTitleSubtitle,
  } from "ng-apexcharts";
  import { DrilldownModalComponent } from "./drilldown-modal/drilldown-modal.component";
  import { TranslateService } from "@ngx-translate/core";
  
  // --- Interfaces ---
  export interface StatisticData {
    campaign_uuid: string;
    campaign_name: string;
    organization: string;
    time_group: string; // Format: "YYYY-MM"
    total_minutes: number;
    total_duration: number; // Dodane pole
  }
  
  export interface ApiResponse {
    message: string;
    data: StatisticData[];
    error: string | null;
  }
  
  // --- Service Definition ---
  @Injectable({
    providedIn: "root",
  })
  export class StatisticsService {
    private apiUrl = `${environment.apiUri}/statistics/organisations`;
    constructor(private http: HttpClient) {}
  
    getStatistics(
      start: string,
      end: string,
      groupby: string
    ): Observable<ApiResponse> {
      console.log(
        `Proxy Call: Fetching stats from ${start} to ${end} grouped by ${groupby} via ${this.apiUrl}`
      );
      const headers = new HttpHeaders({
        "Content-Type": "application/json",
        Accept: "application/json",
      });
      const body = { start, end, groupby };
      return this.http
        .post<ApiResponse>(this.apiUrl, body, { headers: headers })
        .pipe(
          catchError((error) => {
            console.error("Proxy API Error fetching statistics:", error);
            const errorResponse: ApiResponse = {
              message:
                error.error?.message || "Failed to fetch statistics via proxy",
              data: [],
              error: error.error?.error || error.message || "Unknown proxy error",
            };
            return of(errorResponse);
          })
        );
    }
  }
  
  // --- ChartOptions Type Definition ---
  export type ChartOptions = {
    series: ApexAxisChartSeries | ApexNonAxisChartSeries;
    chart: ApexChart;
    xaxis: ApexXAxis;
    stroke: ApexStroke;
    dataLabels: ApexDataLabels;
    plotOptions: ApexPlotOptions;
    yaxis: ApexYAxis;
    tooltip: ApexTooltip;
    fill: ApexFill;
    legend?: ApexLegend;
    colors?: string[];
    states?: ApexStates;
    responsive?: ApexResponsive[];
    title?: ApexTitleSubtitle;
  };
  
  // --- OrgDataWithTrend Interface ---
  interface OrgDataWithTrend {
    orgName: string;
    trend: number;
    dataMap: Map<string, number>;
    color?: string;
  }
  
  // --- Component Definition ---
  @Component({
    selector: "app-statistics-chart",
    templateUrl: "./statistics-chart.component.html",
    styleUrls: ["./statistics-chart.component.scss"],
  })
  export class StatisticsChartComponent implements OnInit, OnDestroy {
    // --- Chart Properties ---
    @ViewChild("statsChart") private chartComponent!: ChartComponent;
    public chartOptions!: Partial<ChartOptions>;
    @ViewChild("statsChartDuration")
    private chartComponentDuration!: ChartComponent;
    public chartOptionsDuration!: Partial<ChartOptions>;
    public isLoading: boolean = false;
    public errorMessage: string | null = null;
    public currentPeriod: string = "month";
    private defaultColors: string[] = [];
    public isCollapsed: boolean = true;
    public allOrganizations: string[] = [];
    public selectedOrganizations: { [orgName: string]: boolean } = {};
    public initialOrgLoadComplete: boolean = false;
    private dataSubscription: Subscription | undefined;
    public lastRawData: StatisticData[] = [];
    public lastProcessedOrgs: OrgDataWithTrend[] = [];
    public lastProcessedOrgsDuration: OrgDataWithTrend[] = [];
    private modalRef: NgbModalRef | undefined;
  
    constructor(
      private statisticsService: StatisticsService,
      private translate: TranslateService,
      private cdRef: ChangeDetectorRef,
      private modalService: NgbModal
    ) {
      this.initializeChartOptions();
      this.initializeChartOptionsDuration();
    }
  
    ngOnInit() {
      this.changePeriod("month");
    }
  
    ngOnDestroy() {
      this.dataSubscription?.unsubscribe();
      this.modalRef?.close();
    }
  
    // --- Data Fetching and Processing ---
    changePeriod(period: "month" | "3months" | "6months" | "year") {
      if (this.isLoading) return;
      this.currentPeriod = period;
      const { start, end } = this.calculateDateRange(period);
      console.log(`📅 Fetching data for period ${period}: ${start} to ${end}`);
      this.loadChartData(start, end, "month");
    }
  
    loadChartData(startDate: string, endDate: string, groupby: string) {
      console.log("⏳ Calling getStatistics service:", {
        startDate,
        endDate,
        groupby,
      });
      this.isLoading = true;
      this.errorMessage = null;
      this.lastProcessedOrgs = [];
      this.lastProcessedOrgsDuration = [];
  
      this.dataSubscription?.unsubscribe();
  
      this.dataSubscription = this.statisticsService
        .getStatistics(startDate, endDate, groupby)
        .subscribe({
          next: (response) => {
            console.log("✅ API response received");
            this.isLoading = false;
  
            if (response.error) {
              console.warn("⚠️ API error message:", response.error);
              this.errorMessage =
                response.message || response.error || "Unknown error from API";
              this.lastRawData = [];
            } else {
              this.errorMessage = null;
              this.lastRawData = response.data || [];
              console.log("📦 Data loaded:", this.lastRawData.length);
  
              if (!this.initialOrgLoadComplete) {
                if (this.lastRawData.length > 0) {
                  this.populateInitialOrganizations(this.lastRawData);
                } else {
                  this.allOrganizations = [];
                  this.selectedOrganizations = {};
                }
                this.initialOrgLoadComplete = true;
              }
            }
            setTimeout(() => {
              this.processAndDisplayChart(this.lastRawData);
              this.cdRef.detectChanges();
            }, 0);
          },
          error: (err) => {
            console.error("❌ HTTP call failed:", err);
            this.errorMessage = "Failed to connect to the backend service.";
            this.isLoading = false;
            this.lastRawData = [];
            if (!this.initialOrgLoadComplete) {
              this.allOrganizations = [];
              this.selectedOrganizations = {};
              this.initialOrgLoadComplete = true;
            }
            this.processAndDisplayChart([]);
            this.cdRef.detectChanges();
          },
          complete: () => {
            console.log("📍 Observable complete");
            this.isLoading = false;
            this.cdRef.detectChanges();
          },
        });
    }
  
    populateInitialOrganizations(data: StatisticData[]) {
      const orgSet = new Set<string>(data.map((d) => d.organization));
      this.allOrganizations = Array.from(orgSet).sort((a, b) =>
        a.localeCompare(b)
      );
      const previousSelection = { ...this.selectedOrganizations };
      this.selectedOrganizations = {};
      this.allOrganizations.forEach((org) => {
        this.selectedOrganizations[org] = previousSelection[org] ?? true;
      });
      console.log("👥 Initial organizations populated:", this.allOrganizations);
    }
  
    processAndDisplayChart(rawData: StatisticData[]) {
      console.log("🔄 Przetwarzanie danych dla obu wykresów...");
      this.lastProcessedOrgs = [];
      this.lastProcessedOrgsDuration = [];
  
      if (!this.initialOrgLoadComplete) {
        console.log(
          "⏳ Filtry niezaładowane, opóźnianie przetwarzania wykresów."
        );
        return;
      }
  
      const activeOrganizations = Object.entries(this.selectedOrganizations)
        .filter(([, isSelected]) => isSelected)
        .map(([orgName]) => orgName);
  
      const filteredData = rawData.filter((item) =>
        activeOrganizations.includes(item.organization)
      );
  
      if (filteredData.length === 0) {
        console.log("📊 Brak danych po filtrowaniu organizacji.");
        this.updateChart([], []);
        this.updateChartDuration([], []);
        return;
      }
  
      const monthSet = new Set<string>(filteredData.map((d) => d.time_group));
      const sortedMonths = Array.from(monthSet).sort();
  
      // --- Przetwarzanie dla pierwszego wykresu (total_minutes) ---
      const dataByOrgMinutes = new Map<string, Map<string, number>>();
      filteredData.forEach((item) => {
        if (!dataByOrgMinutes.has(item.organization)) {
          dataByOrgMinutes.set(item.organization, new Map());
        }
        const monthMap = dataByOrgMinutes.get(item.organization)!;
        const currentMinutes = monthMap.get(item.time_group) || 0;
        const minutesToAdd = Number(item.total_minutes) || 0;
        monthMap.set(item.time_group, currentMinutes + minutesToAdd);
      });
  
      this.lastProcessedOrgs = Array.from(dataByOrgMinutes.entries())
        .map(([org, map]) => ({
          orgName: org,
          trend: this.calculateTrendValue(sortedMonths, map),
          dataMap: map,
          color: undefined,
        }))
        .sort((a, b) => a.orgName.localeCompare(b.orgName));
  
      const seriesMinutes: ApexAxisChartSeries = this.lastProcessedOrgs.map(
        (orgData) => ({
          name: orgData.orgName,
          data: sortedMonths.map((month) => orgData.dataMap.get(month) || 0),
        })
      );
  
      // --- Przetwarzanie dla drugiego wykresu (total_duration) ---
      const dataByOrgDuration = new Map<string, Map<string, number>>();
      filteredData.forEach((item) => {
        if (!dataByOrgDuration.has(item.organization)) {
          dataByOrgDuration.set(item.organization, new Map());
        }
        const monthMap = dataByOrgDuration.get(item.organization)!;
        const currentDuration = monthMap.get(item.time_group) || 0;
        const durationToAdd = Number(item.total_duration) || 0;
        monthMap.set(item.time_group, currentDuration + durationToAdd);
      });
  
      this.lastProcessedOrgsDuration = Array.from(dataByOrgDuration.entries())
        .map(([org, map]) => ({
          orgName: org,
          trend: this.calculateTrendValue(sortedMonths, map),
          dataMap: map,
          color: undefined,
        }))
        .sort((a, b) => a.orgName.localeCompare(b.orgName));
  
      const seriesDuration: ApexAxisChartSeries =
        this.lastProcessedOrgsDuration.map((orgData) => ({
          name: orgData.orgName,
          data: sortedMonths.map((month) => orgData.dataMap.get(month) || 0),
        }));
  
      console.log("📊 Seria pierwszego wykresu (total_minutes):", seriesMinutes);
      console.log("📊 Seria drugiego wykresu (total_duration):", seriesDuration);
      console.log("📅 Kategorie (xaxis):", sortedMonths);
  
      this.updateChart(sortedMonths, seriesMinutes);
      this.updateChartDuration(sortedMonths, seriesDuration);
    }
  
    // --- Chart Initialization ---
    initializeChartOptions(): void {
      this.defaultColors = [
        "#5B8FF9",
        "#61DDAA",
        "#65789B",
        "#F6BD16",
        "#7262FD",
        "#78D3F8",
        "#9661BC",
        "#F6903D",
        "#008685",
        "#F08BB4",
        "#A2B7C9",
        "#73C2BE",
        "#B28FE8",
        "#F8C955",
        "#D96D6F",
        "#B4A0D1",
        "#85CFCB",
        "#D0A5A5",
      ];
  
      this.chartOptions = {
        series: [],
        chart: {
          type: "bar",
          height: 350,
          stacked: false,
          toolbar: { show: true },
          zoom: { enabled: true },
          events: {
            dataPointSelection: (event, chartContext, config) => {
              this.handleDataPointClick(
                config.seriesIndex,
                config.dataPointIndex
              );
            },
            dataPointMouseEnter: (event: any) => {
              if (event.target) {
                (event.target as HTMLElement).style.cursor = "pointer";
              }
            },
            dataPointMouseLeave: (event: any) => {
              if (event.target) {
                (event.target as HTMLElement).style.cursor = "default";
              }
            },
          },
        },
        colors: this.defaultColors,
        plotOptions: {
          bar: { horizontal: false, columnWidth: "55%", borderRadius: 5 },
        },
        dataLabels: { enabled: false },
        stroke: { show: true, width: 2, colors: ["transparent"] },
        xaxis: {
          categories: [],
          title: { text: this.translate.instant("DASHBOARD.CHART.X_AXIS_MONTH") },
          labels: {},
        },
       yaxis: {
    logarithmic: true,
    min: 1,
    title: {
      text: this.translate.instant("DASHBOARD.CHART.Y_AXIS_TOTAL_BILLED"),
    },
    labels: {
      formatter: (val: number) => (val ? val.toFixed(0) : "0"),
    },
  },
  
  
        fill: { opacity: 1 },
        tooltip: {
          y: {
            formatter: (val: number) => (val ? val.toFixed(0) : "0") + " minut",
          },
        },
        legend: { show: false },
        responsive: [
          {
            breakpoint: 1200,
            options: {
              chart: { height: 500 },
              plotOptions: { bar: { columnWidth: "60%" } },
            },
          },
          {
            breakpoint: 768,
            options: {
              chart: { height: 400 },
              plotOptions: { bar: { columnWidth: "55%" } },
            },
          },
          {
            breakpoint: 600,
            options: {
              chart: { height: 300 },
              plotOptions: { bar: { columnWidth: "50%" } },
            },
          },
        ],
        states: {
          hover: { filter: { type: "lighten", value: 0 } as any },
          active: {
            allowMultipleDataPointsSelection: false,
            filter: { type: "darken", value: 0.8 } as any,
          },
        } as any,
      };
      console.log("📊 Chart options initialized with explicit cursor events.");
    }
  
    initializeChartOptionsDuration(): void {
      this.chartOptionsDuration = {
        series: [],
        chart: {
          type: "bar",
          height: 350,
          stacked: false,
          toolbar: { show: true },
          zoom: { enabled: true },
          events: {
            dataPointSelection: (event, chartContext, config) => {
              this.handleDataPointClickDuration(
                config.seriesIndex,
                config.dataPointIndex
              );
            },
            dataPointMouseEnter: (event: any) => {
              if (event.target) {
                (event.target as HTMLElement).style.cursor = "pointer";
              }
            },
            dataPointMouseLeave: (event: any) => {
              if (event.target) {
                (event.target as HTMLElement).style.cursor = "default";
              }
            },
          },
        },
        colors: this.defaultColors,
        plotOptions: {
          bar: { horizontal: false, columnWidth: "55%", borderRadius: 5 },
        },
        dataLabels: { enabled: false },
        stroke: { show: true, width: 2, colors: ["transparent"] },
        xaxis: {
          categories: [],
          title: { text: this.translate.instant("DASHBOARD.CHART.X_AXIS_MONTH") },
          labels: {},
        },
        yaxis: {
          logarithmic: true,
          min: 1,
          title: {
            text: this.translate.instant("DASHBOARD.CHART.Y_AXIS_TOTAL_DURATION"),
          },
          labels: {
            formatter: (val: number) => (val ? val.toFixed(0) : "0"),
          },
        },
  
        fill: { opacity: 1 },
        tooltip: {
          y: {
            formatter: (val: number) => (val ? val.toFixed(0) : "0") + " minut",
          },
        },
        legend: { show: false },
        responsive: [
          {
            breakpoint: 1200,
            options: {
              chart: { height: 500 },
              plotOptions: { bar: { columnWidth: "60%" } },
            },
          },
          {
            breakpoint: 768,
            options: {
              chart: { height: 400 },
              plotOptions: { bar: { columnWidth: "55%" } },
            },
          },
          {
            breakpoint: 600,
            options: {
              chart: { height: 300 },
              plotOptions: { bar: { columnWidth: "50%" } },
            },
          },
        ],
        states: {
          hover: { filter: { type: "lighten", value: 0 } as any },
          active: {
            allowMultipleDataPointsSelection: false,
            filter: { type: "darken", value: 0.8 } as any,
          },
        } as any,
      };
      console.log("📊 Opcje drugiego wykresu (total_duration) zainicjalizowane.");
    }
  
    // --- Chart Update ---
    updateChart(categories: string[], series: ApexAxisChartSeries) {
      if (this.lastProcessedOrgs) {
        const currentColors = this.chartOptions?.colors || this.defaultColors;
        this.lastProcessedOrgs.forEach((org, index) => {
          if (org) {
            org.color = currentColors[index % currentColors.length];
          }
        });
      }
      this.chartOptions = {
        ...this.chartOptions,
        series: series,
        xaxis: {
          ...this.chartOptions.xaxis,
          categories: categories,
        },
        yaxis: { ...this.chartOptions.yaxis },
        colors: this.chartOptions?.colors || this.defaultColors,
      };
      this.cdRef.detectChanges();
      console.log("🚀 Chart options updated with new data.");
    }
  
    updateChartDuration(categories: string[], series: ApexAxisChartSeries) {
      if (this.lastProcessedOrgsDuration) {
        const currentColors =
          this.chartOptionsDuration?.colors || this.defaultColors;
        this.lastProcessedOrgsDuration.forEach((org, index) => {
          if (org) {
            org.color = currentColors[index % currentColors.length];
          }
        });
      }
      this.chartOptionsDuration = {
        ...this.chartOptionsDuration,
        series: series,
        xaxis: {
          ...this.chartOptionsDuration.xaxis,
          categories: categories,
        },
        yaxis: { ...this.chartOptionsDuration.yaxis },
        colors: this.chartOptionsDuration?.colors || this.defaultColors,
      };
      this.cdRef.detectChanges();
      console.log("🚀 Opcje drugiego wykresu (total_duration) zaktualizowane.");
    }
  
    // --- Event Handlers ---
    selectAllOrgs(): void {
      if (this.isLoading || !this.allOrganizations) return;
      console.log("👥 Selecting all organizations...");
      this.allOrganizations.forEach((org) => {
        this.selectedOrganizations[org] = true;
      });
      this.onOrganizationSelectionChange();
    }
  
    deselectAllOrgs(): void {
      if (this.isLoading || !this.allOrganizations) return;
      console.log("👥 Deselecting all organizations...");
      this.allOrganizations.forEach((org) => {
        this.selectedOrganizations[org] = false;
      });
      this.onOrganizationSelectionChange();
    }
  
    onOrganizationSelectionChange(): void {
      if (this.isLoading) return;
      console.log(
        "👥 Organization checkbox selection changed:",
        this.selectedOrganizations
      );
      this.processAndDisplayChart(this.lastRawData);
    }
  
    // --- Drill-Down Modal Logic ---
    handleDataPointClick(seriesIndex: number, dataPointIndex: number): void {
      if (
        !this.lastProcessedOrgs ||
        seriesIndex >= this.lastProcessedOrgs.length
      ) {
        console.error(
          "Drilldown Error: Cannot find organization for series index:",
          seriesIndex
        );
        return;
      }
      const selectedOrg = this.lastProcessedOrgs[seriesIndex].orgName;
  
      const categories = this.chartOptions?.xaxis?.categories;
      if (!categories || dataPointIndex >= categories.length) {
        console.error(
          "Drilldown Error: Cannot find month category for data point index:",
          dataPointIndex,
          categories
        );
        return;
      }
      const selectedMonth = String(categories[dataPointIndex]);
  
      console.log(
        `🖱️ Chart Drilldown requested for Org: ${selectedOrg}, Month: ${selectedMonth}`
      );
      this.prepareAndOpenModal(selectedOrg, [selectedMonth]);
    }
  
    handleDataPointClickDuration(
      seriesIndex: number,
      dataPointIndex: number
    ): void {
      if (
        !this.lastProcessedOrgsDuration ||
        seriesIndex >= this.lastProcessedOrgsDuration.length
      ) {
        console.error(
          "Drilldown Error: Cannot find organization for series index:",
          seriesIndex
        );
        return;
      }
      const selectedOrg = this.lastProcessedOrgsDuration[seriesIndex].orgName;
  
      const categories = this.chartOptionsDuration?.xaxis?.categories;
      if (!categories || dataPointIndex >= categories.length) {
        console.error(
          "Drilldown Error: Cannot find month category for data point index:",
          dataPointIndex,
          categories
        );
        return;
      }
      const selectedMonth = String(categories[dataPointIndex]);
  
      console.log(
        `🖱️ Drilldown drugiego wykresu dla Org: ${selectedOrg}, Month: ${selectedMonth}`
      );
      this.prepareAndOpenModalDuration(selectedOrg, [selectedMonth]);
    }
  
    handleLegendItemClick(
      orgName: string,
      chartType: "minutes" | "duration" = "minutes"
    ): void {
      let currentMonths: string[] = [];
  
      if (chartType === "minutes") {
        if (
          this.chartOptions?.xaxis?.categories &&
          Array.isArray(this.chartOptions.xaxis.categories)
        ) {
          currentMonths = this.chartOptions.xaxis.categories.map(String);
        }
      } else {
        if (
          this.chartOptionsDuration?.xaxis?.categories &&
          Array.isArray(this.chartOptionsDuration.xaxis.categories)
        ) {
          currentMonths = this.chartOptionsDuration.xaxis.categories.map(String);
        }
      }
  
      if (currentMonths.length === 0) {
        console.warn("Legend Click: Brak dostępnych miesięcy w opcjach wykresu.");
        return;
      }
  
      console.log(
        `🖱️ Legend Drilldown dla Org: ${orgName}, Months: ${currentMonths.join(
          ", "
        )}, Chart: ${chartType}`
      );
      if (chartType === "minutes") {
        this.prepareAndOpenModal(orgName, currentMonths);
      } else {
        this.prepareAndOpenModalDuration(orgName, currentMonths);
      }
    }
  
    prepareAndOpenModal(orgName: string, months: string[]): void {
      const filteredRawData = this.lastRawData.filter(
        (item) =>
          item.organization === orgName && months.includes(item.time_group)
      );
  
      if (filteredRawData.length === 0) {
        console.log(
          `Modal Prep: No raw data found for ${orgName} in months: ${months.join(
            ", "
          )}`
        );
        return;
      }
  
      const campaignMinutes = new Map<string, number>();
      filteredRawData.forEach((item) => {
        const currentMinutes = campaignMinutes.get(item.campaign_name) || 0;
        campaignMinutes.set(
          item.campaign_name,
          currentMinutes + (Number(item.total_minutes) || 0)
        );
      });
  
      const drillDownDetails = Array.from(campaignMinutes.entries())
        .map(([campaign_name, total_minutes]) => ({
          campaign_name,
          total_minutes,
        }))
        .sort((a, b) => b.total_minutes - a.total_minutes);
  
      console.log(
        `Modal Prep: Aggregated data for ${orgName} (${months.length} months):`,
        drillDownDetails
      );
  
      let periodLabel = "";
      if (months.length === 1) {
        periodLabel = months[0];
      } else if (months.length > 1) {
        const sorted = [...months].sort();
        periodLabel = `Okres: ${sorted[0]} - ${sorted[sorted.length - 1]}`;
      } else {
        periodLabel = "Wybrany okres";
      }
  
      if (drillDownDetails.length === 0) {
        console.log(
          `Modal Prep: No aggregated campaign data found for ${orgName} in ${periodLabel}. Modal not opened.`
        );
        return;
      }
  
      this.openDrillDownModal(orgName, periodLabel, drillDownDetails);
    }
  
    prepareAndOpenModalDuration(orgName: string, months: string[]): void {
      const filteredRawData = this.lastRawData.filter(
        (item) =>
          item.organization === orgName && months.includes(item.time_group)
      );
  
      if (filteredRawData.length === 0) {
        console.log(
          `Modal Prep: Brak danych dla ${orgName} w miesiącach: ${months.join(
            ", "
          )}`
        );
        return;
      }
  
      const campaignDuration = new Map<string, number>();
      filteredRawData.forEach((item) => {
        const currentDuration = campaignDuration.get(item.campaign_name) || 0;
        campaignDuration.set(
          item.campaign_name,
          currentDuration + (Number(item.total_duration) || 0)
        );
      });
  
      const drillDownDetails = Array.from(campaignDuration.entries())
        .map(([campaign_name, total_duration]) => ({
          campaign_name,
          total_minutes: total_duration,
        }))
        .sort((a, b) => b.total_minutes - a.total_minutes);
  
      console.log(
        `Modal Prep: Agregowane dane dla ${orgName} (${months.length} miesięcy):`,
        drillDownDetails
      );
  
      let periodLabel = "";
      if (months.length === 1) {
        periodLabel = months[0];
      } else if (months.length > 1) {
        const sorted = [...months].sort();
        periodLabel = `Okres: ${sorted[0]} - ${sorted[sorted.length - 1]}`;
      } else {
        periodLabel = "Wybrany okres";
      }
  
      if (drillDownDetails.length === 0) {
        console.log(
          `Modal Prep: Brak danych kampanii dla ${orgName} w ${periodLabel}. Modal nieotwarty.`
        );
        return;
      }
  
      this.openDrillDownModal(orgName, periodLabel, drillDownDetails);
    }
  
    openDrillDownModal(
      orgName: string,
      periodLabel: string,
      details: { campaign_name: string; total_minutes: number }[]
    ): void {
      this.modalRef?.close();
  
      this.modalRef = this.modalService.open(DrilldownModalComponent, {
        ariaLabelledBy: "modal-basic-title",
        size: "lg",
        scrollable: true,
      });
  
      this.modalRef.componentInstance.organizationName = orgName;
      this.modalRef.componentInstance.month = periodLabel;
      this.modalRef.componentInstance.drillDownData = details;
  
      this.modalRef.result
        .then(
          (result) => {
            console.log(`Modal closed with: ${result}`);
          },
          (reason) => {
            console.log(`Modal dismissed with reason: ${reason}`);
          }
        )
        .finally(() => {
          this.modalRef = undefined;
        });
    }
  
    // --- Utility Functions ---
    calculateTrendValue(months: string[], map: Map<string, number>): number {
      if (months.length < 2) return -Infinity;
      let firstVal: number | null = null;
      let lastVal: number | null = null;
      let firstIndex = -1;
      let lastIndex = -1;
      for (let i = 0; i < months.length; i++) {
        const val = map.get(months[i]);
        if (val !== undefined && val !== null) {
          firstVal = val;
          firstIndex = i;
          break;
        }
      }
      for (let i = months.length - 1; i >= 0; i--) {
        const val = map.get(months[i]);
        if (val !== undefined && val !== null) {
          lastVal = val;
          lastIndex = i;
          break;
        }
      }
      if (firstVal === null || lastVal === null || firstIndex === lastIndex) {
        return -Infinity;
      }
      if (firstVal === 0) {
        return lastVal === 0 ? 0 : Infinity;
      }
      return ((lastVal - firstVal) / firstVal) * 100;
    }
  
    getTrendIndicator(trendValue: number): {
      arrow: string;
      color: string;
      text: string;
    } {
      const positiveColor = "#28a745";
      const negativeColor = "#dc3545";
      const neutralColor = "#6c757d";
      const newOrgColor = "#0dcaf0";
      let arrow = "■";
      let color = neutralColor;
      let text = "■";
      if (trendValue === -Infinity || isNaN(trendValue)) {
    arrow = "■";
    color = neutralColor;
    text = this.translate.instant('DASHBOARD.TREND.NO_DATA');
  } else if (trendValue === Infinity) {
    arrow = "▲";
    color = newOrgColor;
    text = this.translate.instant('DASHBOARD.TREND.NEW');
  } else {
        const change = trendValue;
        arrow = "▶";
        color = neutralColor;
        if (change > 5) {
          color = positiveColor;
          arrow = "▲";
        } else if (change < -5) {
          color = negativeColor;
          arrow = "▼";
        }
        const percentage =
          Math.abs(change) < 0.1
            ? "±0%"
            : `${change > 0 ? "+" : ""}${change.toFixed(0)}%`;
        text = `${arrow} ${percentage}`;
      }
      return { arrow: arrow, color: color, text: text };
    }
  
    calculateDateRange(period: "month" | "3months" | "6months" | "year") {
      const today = new Date();
      let start = new Date(today);
      const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  
      switch (period) {
        case "month":
          start = new Date(today.getFullYear(), today.getMonth(), 1);
          break;
        case "3months":
          start = new Date(today.getFullYear(), today.getMonth() - 2, 1);
          break;
        case "6months":
          start = new Date(today.getFullYear(), today.getMonth() - 5, 1);
          break;
        case "year":
          start = new Date(today.getFullYear(), today.getMonth() - 11, 1);
          break;
      }
  
      return {
        start: this.formatDate(start),
        end: this.formatDate(end),
      };
    }
  
    formatDate(date: Date): string {
      const y = date.getFullYear();
      const m = (date.getMonth() + 1).toString().padStart(2, "0");
      const d = date.getDate().toString().padStart(2, "0");
      return `${y}-${m}-${d}`;
    }
  }
  